import { readStoredRecord } from "@appretio/ui-kit";
import { DROPPED_BLOCK_MARKER } from "./contextBudget";
import type { FeedbackCellContext } from "./FeedbackContext";
import type { FeedbackRestore } from "./hooks/useGetFeedback";

/** The context key a page's own view state rides under. */
export const FEEDBACK_VIEW_KEY = "view";

/** The context key the shared context selector's state rides under. */
export const TOP_BAR_KEY = "top_bar";

/** The context key the keyed grids' views ride under: grid key → view. */
export const GRID_VIEW_KEY = "grid";

/** The context key a "Report this value" cell rides under. */
export const CELL_KEY = "cell";

/** The cell a "Report this value" report names. */
type ReportedValue = Pick<FeedbackCellContext, "field" | "header" | "value">;

/**
 * What a page reads back from a stored feedback report to rebuild the
 * reporter's view. Every part is optional: reports are opaque JSON written by
 * clients of any age, so a block that is missing or misshapen reads as null,
 * and the page restores the rest.
 */
export interface FeedbackViewRestore {
  /** The trimmed report row, for the banner. */
  record: FeedbackRestore;
  /**
   * What the shared context selector showed (`top_bar.selector_draft`):
   * field → selected values. Null when the page had no selector or the report
   * did not capture it (`droppedBlocks` tells which).
   */
  selectorDraft: Record<string, string[]> | null;
  /**
   * The page's own view block (`context.view`). Null on reports written
   * before the page captured one.
   */
  view: Record<string, unknown> | null;
  /**
   * The keyed grids' views (`context.grid`): grid key → view. Null on reports
   * written before the page's grids captured one.
   */
  grids: Record<string, unknown> | null;
  /**
   * The context blocks the size budget replaced (`top_bar`, `view`, `cell`, …):
   * unknown, not absent.
   */
  droppedBlocks: string[];
  /** The reported cell ("Report this value"), when present. */
  cell: ReportedValue | null;
  /** When the report was written, as an unambiguous instant, or null. */
  reportedAt: string | null;
}

/**
 * A stored JSON object, or null. The ui-kit's `readStoredRecord`, re-exported
 * under the `readView*` name the other view readers share.
 */
export const readViewRecord = readStoredRecord;

/**
 * A stored selection — field → list of selected values — keeping only the
 * fields whose value is a list of strings, or null when the value is not an
 * object. Reads `top_bar.selector_draft` and any selection a page puts in its
 * view block.
 */
export function readViewSelection(
  value: unknown
): Record<string, string[]> | null {
  const record = readViewRecord(value);
  if (!record) return null;
  const selection: Record<string, string[]> = {};
  for (const [field, values] of Object.entries(record)) {
    const strings = readViewStrings(values);
    if (strings.length > 0) selection[field] = strings;
  }
  return selection;
}

/** A stored list's string members; anything else reads as empty. */
export function readViewStrings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

/**
 * A stored finite number, or undefined. A numeric string reads as its number:
 * select controls store their values as strings.
 */
export function readViewNumber(value: unknown): number | undefined {
  const number =
    typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  return typeof number === "number" && Number.isFinite(number)
    ? number
    : undefined;
}

/** A non-empty stored string, or undefined. */
export function readViewString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/** A stored boolean, or undefined. */
export function readViewFlag(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

/**
 * A stored timestamp as an unambiguous instant, or null: an ISO date-time
 * that states its zone (trailing `Z` or `±HH[:]MM`). A value without a zone
 * has no knowable zone, so it reads as no instant, not a guessed one.
 */
export function readViewInstant(value: unknown): string | null {
  if (typeof value !== "string" || !/T.*(?:Z|[+-]\d{2}:?\d{2})$/.test(value)) {
    return null;
  }
  return Number.isNaN(new Date(value).getTime()) ? null : value;
}

function parseReportedValue(value: unknown): ReportedValue | null {
  const cell = readViewRecord(value);
  const field = readViewString(cell?.field);
  if (!cell || !field) return null;
  return {
    field,
    header: readViewString(cell.header),
    value: cell.value
  };
}

/**
 * Narrow a stored report into what a page restores from. Never throws: a
 * missing or misshapen block reads as null.
 */
export function parseFeedbackViewRestore(
  record: FeedbackRestore
): FeedbackViewRestore {
  const context = readViewRecord(record.context) ?? {};
  const topBar = readViewRecord(context[TOP_BAR_KEY]);
  return {
    record,
    selectorDraft: readViewSelection(topBar?.selector_draft),
    view: readViewRecord(context[FEEDBACK_VIEW_KEY]),
    grids: readViewRecord(context[GRID_VIEW_KEY]),
    droppedBlocks: Object.keys(context).filter(
      (key) => context[key] === DROPPED_BLOCK_MARKER
    ),
    cell: parseReportedValue(context[CELL_KEY]),
    reportedAt: readViewInstant(record.created_at)
  };
}
